/**
 * SendPricingSheetCard — email the live pricing sheet to ANYONE (no profile
 * needed). Name + email per recipient (personalised "Hi Jane," greeting),
 * optional short note, preview-before-send, running history of every send.
 * Sachin 2026-08-21.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, ChevronUp, Eye, Loader2, Mail, Plus, Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const fmtWhen = (iso) => {
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
        });
    } catch { return iso; }
};

const emptyRow = () => ({ name: "", email: "" });

export default function SendPricingSheetCard() {
    const [rows, setRows] = useState([emptyRow()]);
    const [message, setMessage] = useState("");
    const [previewHtml, setPreviewHtml] = useState(null);
    const [previewing, setPreviewing] = useState(false);
    const [sending, setSending] = useState(false);
    const [sends, setSends] = useState([]);
    const [historyOpen, setHistoryOpen] = useState(false);

    const loadSends = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/coach/pricing-sheet/sends`, { withCredentials: true });
            setSends(r.data.sends || []);
        } catch { /* non-blocking */ }
    }, []);

    useEffect(() => { loadSends(); }, [loadSends]);

    const setRow = (i, patch) =>
        setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
    const removeRow = (i) =>
        setRows((rs) => (rs.length === 1 ? [emptyRow()] : rs.filter((_, idx) => idx !== i)));

    const validRecipients = rows
        .map((r) => ({ name: r.name.trim(), email: r.email.trim().toLowerCase() }))
        .filter((r) => EMAIL_RE.test(r.email));

    const onPreview = async () => {
        setPreviewing(true);
        try {
            const r = await axios.post(
                `${API}/coach/pricing-sheet/preview`,
                { message, name: validRecipients[0]?.name || rows[0]?.name || "" },
                { withCredentials: true },
            );
            setPreviewHtml(r.data.html);
        } catch (e) {
            toast.error("Couldn't build preview", { description: e?.response?.data?.detail || "Unknown error" });
        } finally { setPreviewing(false); }
    };

    const onSend = async () => {
        if (validRecipients.length === 0) {
            toast.error("Enter at least one valid email address");
            return;
        }
        setSending(true);
        try {
            const r = await axios.post(
                `${API}/coach/pricing-sheet/send`,
                { recipients: validRecipients, message },
                { withCredentials: true },
            );
            const { sent = [], failed = [] } = r.data;
            if (sent.length) toast.success(`Pricing sheet sent to ${sent.length} ${sent.length === 1 ? "person" : "people"}`);
            if (failed.length) toast.error(`Failed for: ${failed.map((f) => f.email).join(", ")}`);
            if (sent.length) {
                setRows([emptyRow()]);
                setMessage("");
                setPreviewHtml(null);
                loadSends();
                setHistoryOpen(true);
            }
        } catch (e) {
            toast.error("Send failed", { description: e?.response?.data?.detail || "Unknown error" });
        } finally { setSending(false); }
    };

    return (
        <div data-testid="send-pricing-sheet-card" className="border border-zinc-800 bg-zinc-950/60 p-5 mb-8">
            <div className="flex items-center gap-2 mb-1">
                <Mail className="w-4 h-4 text-brand-red" />
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Send pricing sheet</div>
            </div>
            <p className="text-[11px] text-zinc-500 mb-4">
                Emails the active pricing blocks below to anyone — no profile needed. Their name personalises the greeting ("Hi Jane,").
            </p>

            <div className="space-y-2">
                {rows.map((r, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`pricing-sheet-recipient-row-${i}`}>
                        <input
                            type="text"
                            value={r.name}
                            onChange={(e) => setRow(i, { name: e.target.value })}
                            placeholder="Name"
                            maxLength={80}
                            data-testid={`pricing-sheet-name-input-${i}`}
                            className="w-40 sm:w-48 bg-black border border-zinc-800 px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:border-brand-red focus:outline-none"
                        />
                        <input
                            type="email"
                            value={r.email}
                            onChange={(e) => setRow(i, { email: e.target.value })}
                            placeholder="jane@gmail.com"
                            data-testid={`pricing-sheet-email-input-${i}`}
                            className="flex-1 min-w-0 bg-black border border-zinc-800 px-3 py-2.5 text-sm text-white placeholder-zinc-600 focus:border-brand-red focus:outline-none"
                        />
                        <button
                            type="button"
                            onClick={() => removeRow(i)}
                            title="Remove recipient"
                            data-testid={`pricing-sheet-remove-recipient-${i}`}
                            className="text-zinc-600 hover:text-zinc-300 p-1.5"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>
            <button
                type="button"
                onClick={() => setRows((rs) => [...rs, emptyRow()])}
                data-testid="pricing-sheet-add-recipient"
                className="mt-2 inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-zinc-400 hover:text-white transition-colors"
            >
                <Plus className="w-3.5 h-3.5" /> Add another person
            </button>

            <div className="mt-3">
                <label className="block text-[10px] tracking-[0.18em] uppercase text-zinc-500 mb-1.5">
                    Short message <span className="text-zinc-600 normal-case tracking-normal">(optional — appears above the prices)</span>
                </label>
                <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    rows={2}
                    maxLength={1000}
                    placeholder="Great meeting you at the showcase — here's our pricing!"
                    data-testid="pricing-sheet-message-input"
                    className="w-full bg-black border border-zinc-800 px-3 py-2 text-sm text-white placeholder-zinc-600 focus:border-brand-red focus:outline-none resize-none"
                />
            </div>

            <div className="flex items-center gap-2 mt-3">
                <button
                    type="button"
                    onClick={onPreview}
                    disabled={previewing}
                    data-testid="pricing-sheet-preview-btn"
                    className="inline-flex items-center gap-1.5 border border-zinc-700 text-zinc-200 hover:border-zinc-500 px-4 py-2 text-[11px] tracking-[0.18em] uppercase transition-colors disabled:opacity-50"
                >
                    {previewing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                    Preview
                </button>
                <button
                    type="button"
                    onClick={onSend}
                    disabled={sending || validRecipients.length === 0}
                    data-testid="pricing-sheet-send-btn"
                    className="inline-flex items-center gap-1.5 bg-brand-red hover:bg-brand-red/90 text-white px-4 py-2 text-[11px] tracking-[0.18em] uppercase transition-colors disabled:opacity-40"
                >
                    {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    Send{validRecipients.length > 1 ? ` (${validRecipients.length})` : ""}
                </button>
                {validRecipients.length > 1 && (
                    <span className="text-[10px] text-zinc-500">each person gets their own personalised email</span>
                )}
            </div>

            {/* Preview modal */}
            {previewHtml && (
                <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setPreviewHtml(null)}>
                    <div
                        data-testid="pricing-sheet-preview-modal"
                        className="bg-zinc-950 border border-zinc-800 w-full max-w-2xl max-h-[88vh] flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-900">
                            <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-300">
                                Email preview{validRecipients.length > 1 ? " — shown for the first person; each greeting is personalised" : " — exactly what they'll get"}
                            </div>
                            <button type="button" onClick={() => setPreviewHtml(null)} data-testid="pricing-sheet-preview-close" className="text-zinc-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <iframe
                            title="Pricing email preview"
                            srcDoc={previewHtml}
                            sandbox=""
                            className="w-full flex-1 min-h-[60vh] bg-white"
                        />
                        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-zinc-900">
                            <button
                                type="button"
                                onClick={() => { setPreviewHtml(null); onSend(); }}
                                disabled={sending || validRecipients.length === 0}
                                data-testid="pricing-sheet-preview-send-btn"
                                className="inline-flex items-center gap-1.5 bg-brand-red hover:bg-brand-red/90 text-white px-4 py-2 text-[11px] tracking-[0.18em] uppercase transition-colors disabled:opacity-40"
                            >
                                <Send className="w-3.5 h-3.5" /> Looks good — send{validRecipients.length > 1 ? ` (${validRecipients.length})` : ""}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Running send history */}
            <button
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
                data-testid="pricing-sheet-history-toggle"
                className="mt-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-zinc-400 hover:text-white transition-colors"
            >
                {historyOpen ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                Sent history ({sends.length})
            </button>
            {historyOpen && (
                <div data-testid="pricing-sheet-history" className="mt-2 divide-y divide-zinc-900 border border-zinc-900">
                    {sends.length === 0 ? (
                        <div className="px-3 py-2.5 text-xs text-zinc-500">Nothing sent yet.</div>
                    ) : sends.map((s) => (
                        <div key={s.send_id} className="px-3 py-2 flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-sm text-white truncate">
                                    {s.name ? <>{s.name} <span className="text-zinc-500">· {s.email}</span></> : s.email}
                                </div>
                                {s.message && <div className="text-[11px] text-zinc-500 truncate">"{s.message}"</div>}
                            </div>
                            <div className="text-right shrink-0">
                                <div className="text-[11px] text-zinc-400">{fmtWhen(s.sent_at)}</div>
                                <div className="text-[10px] text-zinc-600">by {s.sent_by_name}</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
